// ============================================================================
// PriceRail — horizontal Stop / Entry / Target gauge.
//
// Renders all three reference prices on a single 6px rail, with the
// "win zone" (entry → target) shaded green. Labels are clamped at the
// edges so left-most reads right-ward and right-most reads left-ward —
// nothing overflows on narrow cards. Currently used by the Dashboard
// FeaturedSignal and the TradingPage position cards so an open position
// reads with the same visual language as the signal that opened it.
//
// `entry` may be either the signal's currentPrice (pre-trade preview) or
// the position's actual entry price (post-trade). All three numbers must
// be finite; the component renders nothing otherwise.
//
// Optional `current` prop overlays an extra marker so an open position
// can show "where am I now" relative to entry / target / stop.
// ============================================================================

function alignFor(p) {
  if (p <= 6)  return { transform: 'translateX(0)',     textAlign: 'left',   left: '0%' };
  if (p >= 94) return { transform: 'translateX(-100%)', textAlign: 'right',  left: '100%' };
  return        { transform: 'translateX(-50%)', textAlign: 'center', left: p + '%' };
}

export default function PriceRail({ entry, target, stop, current, cs = '$' }) {
  if (!Number.isFinite(entry) || !Number.isFinite(target) || !Number.isFinite(stop)) return null;
  // Include `current` in the range so the marker can't fall outside the rail.
  const numbers = [entry, target, stop, ...(Number.isFinite(current) ? [current] : [])];
  const lo = Math.min(...numbers);
  const hi = Math.max(...numbers);
  const pos = (v) => ((v - lo) / (hi - lo || 1)) * 100;

  const items = [
    { label: 'Stop',   val: stop,   c: 'var(--red)',         p: pos(stop) },
    { label: 'Entry',  val: entry,  c: 'var(--accent-soft)', p: pos(entry) },
    { label: 'Target', val: target, c: 'var(--green)',       p: pos(target) },
  ];

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{
        position: 'relative', height: 6,
        borderRadius: 'var(--r-pill)',
        background: 'var(--bg-elevated)',
        marginBottom: 44,
      }}>
        {/* Fill between entry and target (the "win zone") */}
        <div style={{
          position: 'absolute', top: 0, bottom: 0,
          left: Math.min(pos(entry), pos(target)) + '%',
          width: Math.abs(pos(target) - pos(entry)) + '%',
          background: 'var(--grad-success)', opacity: 0.5,
          borderRadius: 'var(--r-pill)',
        }} />

        {items.map((it) => {
          const a = alignFor(it.p);
          return (
            <div key={it.label}>
              <div style={{
                position: 'absolute', left: it.p + '%', top: '50%',
                transform: 'translate(-50%, -50%)', zIndex: 2,
              }}>
                <div style={{
                  width: 12, height: 12, borderRadius: '50%',
                  background: it.c, border: '2px solid var(--bg-card)',
                  boxShadow: `0 0 8px ${it.c}`,
                }} />
              </div>
              <div style={{
                position: 'absolute', top: 16,
                left: a.left, transform: a.transform, textAlign: a.textAlign,
                whiteSpace: 'nowrap',
              }}>
                <div className="eyebrow" style={{ fontSize: 8.5, color: it.c }}>{it.label}</div>
                <div className="mono" style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {cs}{it.val.toFixed(2)}
                </div>
              </div>
            </div>
          );
        })}

        {/* Live "now" marker, drawn over the entry dot. Shown only when
            current is provided AND differs meaningfully from entry (≥ 0.1%)
            so it doesn't visually clutter pre-trade previews. */}
        {Number.isFinite(current) && entry > 0 && Math.abs(current - entry) / entry > 0.001 && (
          <div style={{
            position: 'absolute', left: pos(current) + '%', top: '50%',
            transform: 'translate(-50%, -50%)', zIndex: 3,
          }}>
            <div style={{
              width: 10, height: 10,
              background: 'var(--text-primary)',
              border: '2px solid var(--bg-card)',
              boxShadow: '0 0 0 1px var(--text-primary)',
              transform: 'rotate(45deg)',
            }} title={`Current ${cs}${current.toFixed(2)}`} />
          </div>
        )}
      </div>
    </div>
  );
}
